import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* Import Modern Google Fonts */
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  /* Custom keyframe animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0.95);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }

  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(255, 107, 53, 0.3); }
    50% { box-shadow: 0 0 40px rgba(255, 107, 53, 0.5); }
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  body {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
    font-weight: ${({ theme }) => theme.typography.fontWeights.normal};
    line-height: ${({ theme }) => theme.typography.lineHeights.normal};
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    overscroll-behavior: none;
    
    /* Prevent body scroll when mobile menu is open */
    &.mobile-menu-open {
      overflow: hidden;
      position: fixed;
      width: 100%;
    }
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.fast};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};

    &:hover {
      color: ${({ theme }) => theme.colors.primaryHover};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
    line-height: ${({ theme }) => theme.typography.lineHeights.tight};
    color: ${({ theme }) => theme.colors.text};
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.fontSizes['4xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeights.extrabold};
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  }

  /* Selection styling */
  ::selection {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.textInverse};
  }

  /* Custom scrollbar - modern sleek design */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 100px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  /* Smooth focus states */
  *:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Remove default focus for mouse users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Toast overrides */
  .Toastify__toast {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    border-radius: 12px;
  }

  /* Mobile-specific utility classes */
  .hide-on-mobile {
    @media (max-width: 768px) {
      display: none !important;
    }
  }
  
  .show-on-mobile {
    display: none !important;
    @media (max-width: 768px) {
      display: block !important;
    }
  }
  
  .hide-on-tablet {
    @media (max-width: 1024px) {
      display: none !important;
    }
  }
  
  .show-on-tablet {
    display: none !important;
    @media (max-width: 1024px) {
      display: block !important;
    }
  }
  
  /* Mobile touch-friendly elements */
  @media (max-width: 768px) {
    button, a, input, select, textarea {
      min-height: 44px;
      min-width: 44px;
    }
    
    input, select, textarea {
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }
`;

export default GlobalStyles;
